/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { selectors as ownerSelectors, actions as ownerActions } from '../../../../redux/ducks/owners';
import { getProfile, getMainProfileId, getActiveProfile } from '../../../redux/reduxSelectors';
import NavigationActions, { navigate } from '../../../redux/NavigationActions';

import List, { ListItem } from '../../../../components/List';
import PendingEmail from './PendingEmail';
import ProfileOwnerRow from './ProfileOwnerRow';
import AddButtonTextLink, { AddProfileTextLink } from '../../../../components/buttons/AddButtonTextLink';
import Card, { ExternalCardTitle } from '../../../../components/card/Card';
import { ScrollAwareContext, ScrollAwareScroller } from '../../../../components/ScrollAware';
import ContentFrame from '../../../../applicationFrame/ContentFrame';

import RouteNames from '../../../RouteNames';
import { AppHeaderScrollableText } from '../../../../components/appNav/AppHeaderScrollable';
import ProfileModel from '../../../../../models/ProfileModel';
import AppNavTopLevel from '../../../../components/appNav/AppNavTopLevel';
import ContentWrapper from '../../../../components/ContentWrapper';

interface Props {
  mainProfile: any;
  mainProfileId: number;
  sharedProfileId: number;
  activeOwnerProfiles: any[];
  pendingOwnerNotifications: any[];
  sharedProfile: ProfileModel;
  goToInviteOwner(): void;
  goToInviteSelf(): void;
  cancelOwnerInvite(profileId, notificationId): void;
  resendOwnerInvite(notificationId, cb): void;
  removeOwnership(): void;
  loadProfileData(profileId): void;
}

interface State {
}


const PendingAccess = (props) => {
  const { pendingOwnerNotifications, cancelOwnerInvite, resendOwnerInvite, sharedProfileId } = props;
  if (pendingOwnerNotifications.length === 0) { return null; }

  return (
    <>
      <ExternalCardTitle>
        <FormattedMessage id="access.pendingUserList.label" />
      </ExternalCardTitle>

      <Card className="cmp-profile-access bkgd-color-white">
        <List paddingSize="large" hasBorder>
          {
            pendingOwnerNotifications.map((notification) => {
              return (
                <ListItem key={notification.id}>
                  <PendingEmail
                    email={notification.email}
                    expirationDate={notification.data.expires_at}
                    cancel={() => { cancelOwnerInvite(sharedProfileId, notification.id); }}
                    resend={(callback) => { resendOwnerInvite(notification.id, callback); }}
                    isSelf={notification.isSelfInvitation}
                  />
                </ListItem>
              );
            })
          }
        </List>
      </Card>
    </>
  );
};

const ActiveAccess = (props) => {
  const { activeOwnerProfiles, mainProfileId, goToInviteOwner, goToInviteSelf, isSharedProfileMain, goToAccessDetails, sharedProfile, allowInviteSelf } = props;
  const sharedProfileId = sharedProfile.data.id;

  return (
    <>
      <ExternalCardTitle>
        <FormattedMessage id="access.user.userList.label" />
      </ExternalCardTitle>

      <Card className="cmp-profile-access bkgd-color-white">
        <List paddingSize="large" hasBorder>
          {
            activeOwnerProfiles.map((profile) => {
              if (profile.data.id === mainProfileId) { return null; }
              const onEditAccess = isSharedProfileMain ? () => { goToAccessDetails(profile.data.id); } : null;

              return (
                <ListItem
                  key={profile.data.id}
                  onClick={onEditAccess}
                >
                  <ProfileOwnerRow
                    allowEdit={!!onEditAccess}
                    profile={profile}
                    isMainProfile={false}
                    isSelf={sharedProfileId === profile.data.id}
                  />
                </ListItem>
              );
            })
          }

          {
            allowInviteSelf && (
              <li>
                <AddProfileTextLink
                  message={<FormattedMessage id="access.inviteSelf.button.label" values={{ string1: sharedProfile.greetingName() }} />}
                  className="font-footnote font-color-profile-accent"
                  onClick={goToInviteSelf}
                />
              </li>
            )
          }

          <li>
            <AddButtonTextLink
              messageId="profileAccess.inviteCoOwner.button.label"
              className="font-footnote font-color-profile-accent"
              onClick={goToInviteOwner}
            />
          </li>
        </List>
      </Card>
    </>
  );
}

@connect(mapStateToProps, mapDispatchToProps)
export class ViewProfileAccess extends React.Component<Props, State> {
  state = {
    isSendingEmail: false,
  };

  goToAccessDetails = (ownerProfileId) => {
    navigate(RouteNames.profileAccessPermissions(ownerProfileId));
  }

  render() {
    const { sharedProfile } = this.props;

    const sharedProfileId = sharedProfile.data.id;
    const isSharedProfileMain = this.props.mainProfileId === sharedProfileId;
    const isSelfClaimed = this.props.pendingOwnerNotifications.some(n => n.isSelfInvitation) || this.props.activeOwnerProfiles.some(n => n.data.id === sharedProfileId);
    const canSelfConsent = sharedProfile.data.can_self_consent;
    const titleMsg = isSharedProfileMain ? { id: 'access.user.title.text' } : { id: 'access.notUser.title.text', values: { string1: sharedProfile.greetingName() } };

    return (
      <div>
        <ScrollAwareContext>
          <AppNavTopLevel hasDropShadow />

          <ContentFrame className="bkgd-color-light-neutral">
            <ScrollAwareScroller>
              <AppHeaderScrollableText {...titleMsg} />

              <ContentWrapper>
                <Card>
                  <List paddingSize="large" hasBorder={false}>
                    <ListItem
                      onClick={() => {
                        this.goToAccessDetails(this.props.mainProfile.data.id);
                      }}
                    >
                      <ProfileOwnerRow
                        profile={this.props.mainProfile}
                        isMainProfile={true}
                        isSelf={false}
                        allowEdit={true}
                      />
                    </ListItem>
                  </List>
                </Card>

                <ActiveAccess
                  activeOwnerProfiles={this.props.activeOwnerProfiles}
                  mainProfileId={this.props.mainProfileId}
                  goToInviteOwner={this.props.goToInviteOwner}
                  goToInviteSelf={this.props.goToInviteSelf}
                  isSharedProfileMain={isSharedProfileMain}
                  goToAccessDetails={this.goToAccessDetails}
                  sharedProfile={sharedProfile}
                  allowInviteSelf={(!isSelfClaimed && canSelfConsent) && sharedProfile.isDependent()}
                />

                <PendingAccess
                  pendingOwnerNotifications={this.props.pendingOwnerNotifications}
                  cancelOwnerInvite={this.props.cancelOwnerInvite}
                  resendOwnerInvite={this.props.resendOwnerInvite}
                  sharedProfileId={sharedProfileId}
                />
              </ContentWrapper>
            </ScrollAwareScroller>
          </ContentFrame>
        </ScrollAwareContext>
      </div>
    );
  }

  componentWillMount() {
    this.props.loadProfileData(this.props.sharedProfile.data.id);
  }
}

function mapStateToProps(state, routerProps): Partial<Props> {
  const history = routerProps.history;
  const mainProfileId = getMainProfileId(state);
  const mainProfile = getProfile(mainProfileId, state);
  const sharedProfile = getActiveProfile(state);
  const activeOwnerProfiles = ownerSelectors.getActiveOwnersForProfile(sharedProfile.data.id, state);
  const sortedOwnerProfiles = activeOwnerProfiles.sort((a, b) => {
    if (a.data.id === mainProfileId) {
      return -1;
    } else if (b.data.id === mainProfileId) {
      return 1;
    } else {
      return a.fullName() < b.fullName();
    }
  });

  return {
    mainProfile,
    mainProfileId,
    sharedProfile,
    activeOwnerProfiles: sortedOwnerProfiles,
    pendingOwnerNotifications: ownerSelectors.getPendingOwnerNotificationsForProfile(sharedProfile.data.id, state),
    goToInviteOwner: () => {
      NavigationActions.goTo(history, RouteNames.profileInviteOwner());
    },
    goToInviteSelf: () => {
      NavigationActions.goTo(history, RouteNames.profileInviteSelf());
    },
    removeOwnership: () => {
      NavigationActions.goTo(history, RouteNames.profileRemoveOwnership());
    },
  };
}

function mapDispatchToProps(dispatch) {
  return {
    cancelOwnerInvite: (profileId, notificationId) => {
      dispatch(ownerActions.cancelOwnerInvite(profileId, notificationId));
    },
    resendOwnerInvite: (notificationId, callback) => {
      dispatch(ownerActions.resendOwnerInvite(notificationId, callback));
    },
    loadProfileData: profileId => {
      dispatch(ownerActions.requestOwnersForProfile(profileId));
    },
  };
}

export default ViewProfileAccess;
